import { FormattedMessage } from 'components/shared/Intl';
import Highcharts from 'highcharts';
import { useSgBsTheme } from 'hooks/useSgBsTheme';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';
import styled from 'styled-components';

import type { MifidProps } from './Mifid.connect';
import 'highcharts/highcharts-more';
import 'highcharts/modules/exporting';

import type { MifidWidgetProps } from './mifid2-widget';
import { Mifid2 } from './mifid2-widget';

(window as any).Highcharts = Highcharts;

const StyledChart = styled.div`
  .highcharts-dark,
  .highcharts-light,
  .highcharts {
    --highcharts-color-0: #ff8f9d;
    --highcharts-color-1: #ff8f9d;
    --highcharts-color-2: #ffe977;
    --highcharts-color-3: #ffe977;
    --highcharts-color-4: #96ff5f;
    --highcharts-color-5: #96ff5f;
    --highcharts-color-6: #777;
    --highcharts-color-7: blue;
  }
  .highcharts-series-7 {
    stroke-dasharray: 2, 3;
  }
`;
const Component: FC<MifidProps> = ({ tabId, containerId, productGovernanceAssessment }) => {
  const [isModalDisplayed, setIsModalDisplayed] = useState(false);
  const [isMifidAlreadyRender, setIsMifidAlreadyRender] = useState<any>(null);
  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);
  const theme = useSgBsTheme();
  const onTrigger = useCallback(() => {
    setIsModalDisplayed(!isModalDisplayed);
    logAnalytics.trackEvent(logAnalyticsCategory, 'Target market Map');
  }, [isModalDisplayed, logAnalyticsCategory]);

  const modalId = 'mifid2-modal';

  const options = useMemo(() => {
    return {
      container: containerId + tabId,
      options: {
        legend: true,
        clientStatus: true,
        chart: false,
        theme,
      },
      maps: productGovernanceAssessment.maps,
      client: productGovernanceAssessment.client,
    } satisfies MifidWidgetProps;
  }, [containerId, productGovernanceAssessment.client, productGovernanceAssessment.maps, tabId, theme]);

  const modalOptions = useMemo(() => {
    return {
      ...options,
      container: modalId + containerId + tabId,
      options: {
        legend: true,
        clientStatus: true,
        chart: true,
        theme,
      },
    } satisfies MifidWidgetProps;
  }, [containerId, options, tabId, theme]);

  useEffect(() => {
    // new Mifid2() replace div "id = container" with the mifid
    if (options.maps) {
      if (isModalDisplayed) {
        // tslint:disable-next-line:no-unused-expression
        setTimeout(() => new Mifid2(modalOptions), 0);
      } else {
        // tslint:disable-next-line:no-unused-expression
        if (isMifidAlreadyRender === null) {
          setIsMifidAlreadyRender(new Mifid2(options));
        }
      }
    }
  }, [isModalDisplayed, isMifidAlreadyRender, options, modalOptions]);

  return (
    <>
      {options.maps && (
        <div id={containerId + tabId} data-e2e="mifid2-target-market-result">
          <div className="d-flex flex-row-reverse">
            <button
              analytics-category="Regulation"
              analytics-event="Target market Map"
              analytics-on="click"
              className="btn btn-flat-primary btn-icon-end"
              data-e2e="pg-map-button"
              onClick={onTrigger}
              type="button"
            >
              <FormattedMessage id="target-market-map-button-label" />
              <i className="icon icon-md">launch</i>
            </button>
          </div>
        </div>
      )}
      <span>
        <h5>Performance Simulations {'&'} Probabilities</h5>
      </span>
      <table
        className="table table-striped table-sm"
        id="product-analysisTable"
        data-e2e="mifid2-target-market-result-table"
      >
        <thead>
          <tr>
            <th></th>
            <th>Positive</th>
            <th>Neutral</th>
            <th>Negative</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td> {'>'} Issue Price </td>
            <td>{productGovernanceAssessment?.productAnalysis?.higherThan100?.positive} %</td>
            <td>{productGovernanceAssessment?.productAnalysis?.higherThan100?.neutral} %</td>
            <td>{productGovernanceAssessment?.productAnalysis?.higherThan100?.negative} %</td>
          </tr>
          <tr>
            <td> {'='} Issue Price</td>
            <td>{productGovernanceAssessment?.productAnalysis?.equalTo100?.positive} %</td>
            <td>{productGovernanceAssessment?.productAnalysis?.equalTo100?.neutral} %</td>
            <td>{productGovernanceAssessment?.productAnalysis?.equalTo100?.negative} %</td>
          </tr>
          <tr>
            <td> {'<'} Issue Price </td>
            <td>{productGovernanceAssessment?.productAnalysis?.lessThan100?.positive} %</td>
            <td>{productGovernanceAssessment?.productAnalysis?.lessThan100?.neutral} %</td>
            <td>{productGovernanceAssessment?.productAnalysis?.lessThan100?.negative} %</td>
          </tr>
          <tr>
            <td> {'>'} Benchmark </td>
            <td>{productGovernanceAssessment?.productAnalysis?.pirrBenchMark?.positive} %</td>
            <td>{productGovernanceAssessment?.productAnalysis?.pirrBenchMark?.neutral} %</td>
            <td>{productGovernanceAssessment?.productAnalysis?.pirrBenchMark?.negative} %</td>
          </tr>
        </tbody>
      </table>

      <Modal
        backdrop="static"
        className={`${modalId}-component`}
        fade={false}
        show={isModalDisplayed}
        size="lg"
        scrollable={true}
        data-e2e={modalId}
        id={modalId}
      >
        <ModalHeader>
          <ModalTitle as="h5">
            <FormattedMessage id="sidePanel.modal.mifid2.title" />
          </ModalTitle>
        </ModalHeader>

        <ModalBody>
          <StyledChart id={modalId + containerId + tabId} className="highcharts-dark"></StyledChart>
        </ModalBody>

        <ModalFooter>
          <button className="btn btn-xl btn-default" data-e2e="close-mifid-modal" onClick={onTrigger}>
            <FormattedMessage id="close" />
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

Component.displayName = 'Mifid';

export default Component;
